import { CreateCardCarousel, useCreatePlayer } from '@bpm-web-app/components';
import { GhostComponent, PrimaryPageTitle } from '@bpm-web-app/components/shared';
import { Section } from '@bpm-web-app/create-api-sdk';
import { useGetNewestSoundPackagesByGenres, useGetSections } from '@bpm-web-app/swr-hooks';
import { convertSoundPackageToCarouselProps, createAppRoutes, curatedPackToCreatePlayable, soundPackageToCreatePlayable, useUniqueArray } from '@bpm-web-app/utils';
import { Fragment, useMemo } from 'react';
import styles from './new-packs.module.css';
import { ClickableGenres } from '../../clickable-genres/clickable-genres';

type PacksByGenreCarouselData = {
    [key: string]: ReturnType<typeof convertSoundPackageToCarouselProps>[];
};

export const NewPacks = () => {
    const { data: sectionsData, isLoading: isLoadingSectionsData } = useGetSections('new');
    const { data: packsByGenreData, isLoading: isLoadingByGenreData } = useGetNewestSoundPackagesByGenres();

    const { playDemo } = useCreatePlayer();

    const packsByGenre = useMemo<PacksByGenreCarouselData>(
        () =>
            packsByGenreData?.data?.reduce((prev, current) => {
                const currentPack = convertSoundPackageToCarouselProps(
                    current,
                    <div className="card__darker-padded-description">
                        <ClickableGenres genres={current.Genre} />
                    </div>
                );
                if (prev[`${current.Genre.slug}`]) {
                    prev[`${current.Genre.slug}`].push(currentPack);
                } else {
                    // eslint-disable-next-line no-param-reassign
                    prev[`${current.Genre.slug}`] = [currentPack];
                }
                return prev;
            }, {} as PacksByGenreCarouselData),
        [packsByGenreData?.data]
    );

    const PacksByGenre = useMemo(
        () =>
            packsByGenre
                ? Object.entries(packsByGenre).map(([key, packs]) => (
                    <CreateCardCarousel
                        contentType="pack"
                        key={key}
                        items={packs}
                        cardSize="small"
                        carouselMorePath={packs[0].genre?.name ? createAppRoutes.genresSlug(packs[0].genre.name) : undefined}
                        isLoading={false}
                        carouselTitle={packs[0].genre?.name}
                        onCardPlay={(id) => playDemo(soundPackageToCreatePlayable(packsByGenreData?.data.find((item) => item.id === id)))}
                        itemBasePath={createAppRoutes.packs}
                    />
                ))
                : null,
        [packsByGenre, packsByGenreData?.data, playDemo]
    );

    const isEverythingLoaded = useMemo(() => {
        if (
            !isLoadingByGenreData &&
            !isLoadingSectionsData
        ) return true;
        return false;
    }, [isLoadingByGenreData, isLoadingSectionsData]);

    const ghostElementsKeys = useUniqueArray(5);

    return (
        <>
            <div className={styles['new-packs__header']}>
                <PrimaryPageTitle title="New Packs" />
            </div>
            {isEverythingLoaded ? (
                sectionsData?.data?.map((section) => {
                    if (section.type === Section.TypeEnum.Virtual) {
                        if (section.name === 'Packs By Genre') {
                            return <Fragment key={section.id}>{PacksByGenre}</Fragment>;
                        }
                    }

                    if (section.name === 'Featured Packs') {
                        return (
                            <CreateCardCarousel
                                contentType="pack"
                                key={section.id}
                                items={section.items.map((item) => convertSoundPackageToCarouselProps(item, 'Genre' in item ? <ClickableGenres genres={item.Genre} /> : null))}
                                cardSize="small"
                                carouselMorePath={createAppRoutes.newFeatured}
                                isLoading={false}
                                carouselTitle="Featured"
                                onCardPlay={(id) => playDemo(curatedPackToCreatePlayable(section.items.find((item) => item.id === id)))}
                                itemBasePath={createAppRoutes.packs}
                            />
                        );
                    }
                    return null;
                })
            ) : (
                <>
                    <GhostComponent type="cards" title="Featured" isCreate cardSize="small" linesWidthArray={[100, 80]} key="featured-packs-ghost" elementsCount={10} lineHeight={17} />
                    {ghostElementsKeys.map((keyId) => {
                        return (
                            <>
                                <GhostComponent type="secondary-title" key={`secondary-title-pack-${keyId}`} />
                                <GhostComponent type="cards" key={`ghost-pack-${keyId}`} elementsCount={10} cardSize="small" linesWidthArray={[60, 90]} lineHeight={16} />
                            </>
                        );
                    })}

                </>
            )}
        </>
    );
};
